/**
 * OTRACK-SUBSYSTEM — lifecycle effects for the acquired-point store.
 *
 * Mirrors `usePolarDraftAidEffects.ts`'s extraction rationale: kept OUT of
 * `Scene3DStage.tsx` (near its 2000-line cap) and, per the OTRACK task
 * brief, deliberately NOT wired through `Scene3DStage.tsx`'s
 * `draftAnchored` gate or `pointFilterSession.ts` (N10's concurrent scope).
 *
 * The actual hover-DWELL acquisition timer lives in the non-React pick-loop
 * closure (`interactivePickLoop.ts`'s `resolveAndCacheSnap`) — it needs
 * `snap.snapHit` in `space.spaceId` (the SAME space `otrackLockedCursor`'s
 * constraint math runs in), which is only available inside that closure;
 * the only React-visible overlay field (`snapTarget.world`) is already
 * reprojected to PAPER coords for the marker glyph, a different space
 * inside an active VP. This hook owns everything ELSE:
 *
 *   1. Mounts the module-level Shift-held tracker
 *      ({@link installOtrackShiftTracker}) the dwell timer reads
 *      synchronously (POLARMODE bit 8).
 *   2. Clears every acquired point when the draft session ends
 *      (`isDrafting` → false), OR when the COMMAND changes while a session
 *      stays continuously active (local Codex review round 7 P2 — a
 *      command can be REPLACED by a different command with no intervening
 *      `null` session, which `isDrafting` alone cannot see), OR when the
 *      SAME command RESTARTS (round 10 P2 — the committed point count
 *      DROPS, which can only happen via a genuine new instance) — mirrors
 *      POLAR-DRAFT-END-CLEAR. (Scope note: real AutoCAD also clears
 *      tracking points after EACH point within a multi-point command, e.g.
 *      between LINE vertices — this app clears per-COMMAND instead, a
 *      documented, narrower residual; the per-command clear is what makes
 *      the resolved point's Command-Engine commit — and therefore its undo
 *      — correct regardless.)
 *   3. Clears every acquired point when OTRACKMODE is toggled off.
 *   4. OTRACK-STALE-CACHE — re-resolve the snap at the LAST cursor position
 *      when OTRACKMODE, POLARMODE's OTRACK-related bits, POLARANG, or the
 *      additional-angle-list/basis config, or the acquired-point list itself
 *      change WITHOUT a pointer move (mirrors POLAR's own POLAR-STALE-CACHE
 *      effect in `usePolarDraftAidEffects.ts`, INCLUDING the polar-angle
 *      dependencies — local Codex review round 5 P2, "OTRACK stale cache
 *      misses POLARANG/additional-angle changes": `otrackLockedCursor`
 *      builds its `polarConfig` from the SAME 4 sysvar/pref reads
 *      `usePolarDraftAidEffects` already subscribes to, so a POLARANG edit
 *      while OTRACK uses polar angles (POLARMODE bit 2) must refresh
 *      identically or a click right after the config change can commit a
 *      point constrained under the OLD angle table) — e.g. a
 *      `SETVAR OTRACKMODE 1` typed at the command line, or a point acquired
 *      via the `TT` keyword, must repaint the guide/marker immediately.
 *
 * Local Codex review round 5 P2 ("Clear the OTRACK overlay in the SAME
 * lifecycle path that clears the store") — effects 2 and 3 above used to
 * clear ONLY the zustand store, relying on `refreshSnapAtLastPointerRef` (a
 * pointer-move re-resolve) to repaint the overlay in sync. That refresh is a
 * NO-OP off-canvas / before the first move (mirrors POLAR's own documented
 * limitation) — if the draft ends (or OTRACKMODE turns off) while the
 * cursor is off-canvas, the store empties but the persistent "+" marker (and
 * any live guide/tip) stays rendered at its last position until the mouse
 * re-enters. Both clearing effects now ALSO clear the overlay fields
 * directly via `setOverlayState`, mirroring `usePolarDraftAidEffects`'s
 * POLAR-DRAFT-END-CLEAR effect exactly (which clears `polarGuide`/
 * `polarTip` the same way, for the same reason).
 */

import { useEffect, useRef, type Dispatch, type MutableRefObject, type SetStateAction } from "react";

import {
  useSysvarStore,
  selectEffectiveOtrack,
  selectOtrackUsesPolarAngles,
  selectOtrackShiftToAcquire,
  selectPolarAddAngEnabled,
  selectPolarAngleBasis,
} from "../../dynamicInput/sysvarStore";
import { usePolarTrackingPrefs } from "../../state/polarTrackingPrefs";
import { useOtrackAcquisitionStore } from "../../state/otrackAcquisitionStore";
import { installOtrackShiftTracker } from "../snap/otrackShiftTracker";
import type { OverlayState } from "../overlays";
import type { DraftCommandId } from "../../scene/draftTypes";

interface UseOtrackLifecycleEffectsArgs {
  /** Whether a draft command is currently active (`draftSession != null`). */
  isDrafting: boolean;
  /**
   * Current draft session's command id, or `null` (local Codex review round
   * 7 P2 — "Clear acquisitions when the draft command changes"): a command
   * can be REPLACED by a different command with no intervening `null`
   * session (a hot command-to-command swap), which leaves `isDrafting`
   * continuously `true` — the draft-end effect below alone would then never
   * clear the acquired-point store, letting a point acquired for the OLD
   * command constrain the NEW command's first point. Deliberately just the
   * command id (NOT the fuller `draftGenerationRef` — see
   * `useDraftGenerationRef`'s doc — which ALSO bumps on every committed
   * POINT within the SAME command): acquired points are documented to
   * persist across multiple points of the SAME command (the
   * OTRACK-PER-POINT-CLEAR-S1 residual), so this clear must fire ONLY on a
   * genuine command change, never on an ordinary same-command point advance.
   */
  draftCommand: DraftCommandId | null;
  /**
   * Current draft session's COMMITTED point count, or `0` when idle (local
   * Codex review round 10 P2 — "Clear acquisitions on same-command
   * restarts"): restarting the SAME command while it is already active
   * (e.g. finishing a LINE, then immediately starting a brand-new LINE via
   * the ribbon/toolbar, with no intervening `null` session) leaves BOTH
   * `isDrafting` true AND `draftCommand` unchanged — neither existing
   * trigger fires, so the OLD command's acquired points leak into the NEW
   * instance's first point. A FRESH session always starts at
   * `points.length === 0`, and an ONGOING session's `points` array only
   * ever GROWS (this app has no mid-draft undo — nothing is committed to
   * the document until the command completes) — so a DECREASE, unlike an
   * increase, can only mean a genuine new instance replaced the old one.
   * Deliberately keyed on a decrease (not just "changed") to preserve the
   * SAME per-command (not per-point) persistence contract `draftCommand`
   * already protects: an ordinary same-command point advance only ever
   * INCREASES this value.
   *
   * Optional (unlike `draftCommand`, `isDrafting` etc. in this same
   * interface) — defaults to `0` internally, which never decreases and so
   * never fires this trigger, keeping every PRE-round-10 call site
   * (including the many existing tests unrelated to this specific finding)
   * byte-identical without touching each one. The one production call site
   * (`Scene3DStage.tsx`) always supplies the real value.
   */
  draftPointCount?: number;
  /** Carrier for `installStageInteractions().refreshSnapAtLastPointer`. */
  refreshSnapAtLastPointerRef: MutableRefObject<(() => void) | null>;
  /**
   * Overlay state setter (local Codex review round 5 P2) — clears
   * `otrackGuides`/`otrackMarkers`/`otrackTip` in the SAME lifecycle path
   * that clears the acquired-point store, instead of relying solely on a
   * pointer-move re-resolve that no-ops off-canvas.
   */
  setOverlayState: Dispatch<SetStateAction<OverlayState>>;
}

/**
 * Clears the acquired-point store AND the overlay fields it drives, in one
 * call — the shared body for BOTH clearing effects below (draft-end,
 * OTRACKMODE-off), so the store and the overlay can never drift out of sync
 * with each other regardless of which effect fired.
 */
function clearAcquiredPointsAndOverlay(
  setOverlayState: Dispatch<SetStateAction<OverlayState>>,
): void {
  useOtrackAcquisitionStore.getState().clearAcquired();
  setOverlayState((prev) =>
    (prev.otrackGuides == null || prev.otrackGuides.length === 0) &&
    prev.otrackMarkers == null &&
    prev.otrackTip == null
      ? prev
      : { ...prev, otrackGuides: null, otrackMarkers: null, otrackTip: null },
  );
}

export function useOtrackAcquisitionEffects({
  isDrafting,
  draftCommand,
  draftPointCount = 0,
  refreshSnapAtLastPointerRef,
  setOverlayState,
}: UseOtrackLifecycleEffectsArgs): void {
  const effectiveOtrack = useSysvarStore(selectEffectiveOtrack);
  const otrackUsesPolarAngles = useSysvarStore(selectOtrackUsesPolarAngles);
  const otrackShiftToAcquire = useSysvarStore(selectOtrackShiftToAcquire);
  const acquiredCount = useOtrackAcquisitionStore((s) => s.acquiredPoints.length);
  // OTRACK-SUBSYSTEM (local Codex review round 5 P2) — the SAME polar-config
  // reads `otrackLockedCursor` folds into its `polarConfig` (POLARANG, the
  // additional-angle list + its enable bit, and the angle-measurement
  // basis), mirroring `usePolarDraftAidEffects`'s own POLAR-STALE-CACHE
  // dependency list exactly.
  const polarAng = useSysvarStore((s) => s.values.POLARANG);
  const polarUseAdditional = useSysvarStore(selectPolarAddAngEnabled);
  const polarAddAnglesKey = usePolarTrackingPrefs((s) => s.additionalAnglesDeg.join(","));
  const polarAngleBasis = useSysvarStore(selectPolarAngleBasis);

  // Local Codex review round 9 P2 ("Refresh snap when Shift-to-acquire is
  // pressed") — re-resolve at the last cursor position on every genuine
  // Shift press/release, the SAME re-resolve every other OTRACK stale-cache
  // trigger already uses. `refreshSnapAtLastPointerRef` is a ref (its
  // `.current` changes as the pick loop mounts/remounts), so this passes a
  // stable wrapper that reads it fresh at call time, never a stale mount-
  // time snapshot.
  useEffect(
    () => installOtrackShiftTracker(() => refreshSnapAtLastPointerRef.current?.()),
    [refreshSnapAtLastPointerRef],
  );

  // Local Codex review round 7 P2 — tracks the PREVIOUS command id so a
  // hot command-to-command swap (isDrafting stays `true` throughout) is
  // detected the same way an ordinary draft-end is. Round 10 P2 ALSO tracks
  // the previous point count: a same-command RESTART (e.g. re-clicking the
  // LINE ribbon button while a LINE is already active) leaves `command`
  // unchanged too, so a DECREASE in the committed point count (only
  // possible via a genuine new instance — see `draftPointCount`'s own doc)
  // is the third, final trigger.
  const prevDraftCommandRef = useRef(draftCommand);
  const prevDraftPointCountRef = useRef(draftPointCount);
  useEffect(() => {
    const commandChanged = draftCommand !== prevDraftCommandRef.current;
    const pointCountDecreased = draftPointCount < prevDraftPointCountRef.current;
    prevDraftCommandRef.current = draftCommand;
    prevDraftPointCountRef.current = draftPointCount;
    if (!isDrafting || commandChanged || pointCountDecreased) {
      clearAcquiredPointsAndOverlay(setOverlayState);
    }
  }, [isDrafting, draftCommand, draftPointCount, setOverlayState]);

  useEffect(() => {
    if (!effectiveOtrack) clearAcquiredPointsAndOverlay(setOverlayState);
  }, [effectiveOtrack, setOverlayState]);

  useEffect(() => {
    refreshSnapAtLastPointerRef.current?.();
  }, [
    effectiveOtrack,
    otrackUsesPolarAngles,
    otrackShiftToAcquire,
    polarAng,
    polarUseAdditional,
    polarAddAnglesKey,
    polarAngleBasis,
    acquiredCount,
    refreshSnapAtLastPointerRef,
  ]);
}
